/**
 * FAN-OUT: how a wall asks a route that takes ONE value per axis a question that
 * holds several.
 *
 * Verified against creative-os-backend@origin/main before this was written.
 * `v_FeedQuery.type` is a bare `z.string()` that `TrendRepository.buildFeedWhereSql`
 * writes straight into `t.type = '<value>'`, so a comma-joined list matches zero
 * rows; `platform` is `z.enum(["tiktok","instagram"])` and a list is a 400.
 * `v_ListQuery` on References types BOTH `type` and `platform` as zod enums.
 * Neither route takes a set on any axis, and no backend change is in scope.
 *
 * So a multi-pick is asked ONE REQUEST PER PICKED VALUE, in parallel, and the
 * answers are merged here. What that buys is the whole point of the exercise:
 * every constituent is a server-side read over the WHOLE library, so a second
 * pick can only ever ADD rows. The page-scoped narrowing this replaces filtered
 * the rows one request had already returned, which could drop a match that lived
 * on page two and make the count FALL as the question got wider.
 *
 * THREE THINGS THIS FILE REFUSES TO GUESS
 *
 *  - It never prints a total it did not read. A constituent whose library holds
 *    more than the one window read for it makes the whole figure a FLOOR, and
 *    the readout says "at least" rather than printing a number as if it were
 *    settled.
 *  - It never draws a half-merged wall. A constituent with no answer yet means
 *    the merged set is UNREAD, not empty, so the caller shows its skeleton
 *    rather than a wall that is about to grow under the reader.
 *  - It never hides a failure. Some-but-not-all constituents failing leaves a
 *    real wall that is missing rows, and the caller has to say so out loud.
 */

/**
 * The most requests one wall state may issue at once.
 *
 * Both walls sit under it by construction today: Trends fans out over the cross
 * product of four kinds and two sources (8), References over five kinds (5). So
 * it is a ceiling nobody can reach from the UI rather than a limit anyone will
 * hit. It exists because a fifth trend kind or a third platform would silently
 * make it reachable, and a wall quietly issuing sixteen requests would be a
 * worse bug than the one this file fixes. Past it the plan is CUT, and a cut
 * plan makes the count a floor exactly like an unread window does.
 */
export const MAX_FANOUT = 8;

/** What one request of a fan-out contributed. */
export interface FanoutPart<T> {
  /** The rows this request answered with, already in the route's own order. */
  rows: readonly T[];
  /** What the route said the whole library holds for THIS request. null while it
   *  has not answered, and null when it failed: a figure from a read that did
   *  not happen is the fake number every surface here exists to avoid. */
  total: number | null;
  /** The library holds more for this request than the one window read for it. */
  truncated: boolean;
  failed: boolean;
  /** No answer in hand yet, so this constituent contributes nothing. */
  pending: boolean;
  fetching: boolean;
  refetch: () => void;
}

/** The merged answer, before the page slices it. */
export interface FanoutMerge<T> {
  /** Every row any constituent answered with, deduped by id and in the ORDER THE
   *  ROUTE WOULD HAVE PUT THEM IN had one request answered the whole question:
   *  newest first normally, best match first when the question was a search.
   *  NOT paged: the wall pages this itself, because the constituents paginated
   *  independently and their pages do not line up. */
  rows: T[];
  /** The figure the readout prints. */
  count: number;
  /** `count` is a FLOOR rather than a library-wide match count, so every surface
   *  that prints it has to qualify it. */
  atLeast: boolean;
  isLoading: boolean;
  isFetching: boolean;
  /** EVERY constituent failed: there is no wall to draw at all. */
  isError: boolean;
  /** How many constituents failed while others answered. Above zero the wall is
   *  real but incomplete, and the page says which. */
  failed: number;
  refetch: () => void;
}

/**
 * Merge the answers into one wall.
 *
 * WHY THE COUNT CANNOT FALL WHEN THE SELECTION WIDENS.
 *
 * The set of rows one request can contribute is fixed by that request alone: it
 * is the first `window` rows the route holds for that value, in the route's
 * order, and it does not depend on what else is picked. Widening an axis only
 * ADDS requests, so the merged set with the wider selection is a superset of the
 * merged set with the narrower one, and `rows.length` is monotonic.
 *
 * The floor is monotonic for the same reason on the other term: `max(totals)`
 * can only rise as requests are added, and one of the totals in it is the very
 * total the single-request path prints on its own. So going from one pick (which
 * prints the route's exact library-wide total) to two cannot print a smaller
 * figure, whether or not either read was truncated.
 *
 * When nothing was truncated, cut or failed, every total is under its window and
 * every matching row was therefore read, so `rows.length` is the EXACT
 * library-wide match count and `atLeast` is false — which is what lets a
 * multi-pick keep saying "4 of 15" instead of falling back to bare matches.
 */
export function mergeFanout<T>(
  parts: readonly FanoutPart<T>[],
  idOf: (row: T) => string,
  /** Epoch milliseconds. Newest first is the default order on both walls. */
  timeOf: (row: T) => number,
  /** True when the plan was cut to MAX_FANOUT, so some combination of the picked
   *  values was never asked for at all. */
  capped = false,
  /**
   * SEARCH MODE: the score the ROUTE ranked by, descending, instead of recency.
   *
   * Without it a fan-out silently destroys a relevance ranking. Every constituent
   * comes back in the server's cosine order, and re-sorting the union by `createdAt`
   * throws that away — so picking a kind or a source filter while a search is active
   * used to flip the wall back to newest-first and the readout kept claiming an order
   * the rows no longer had.
   *
   * SAME INVARIANT AS `timeOf`: the key has to exist on every row, so pass this ONLY
   * when every constituent request carried the search that produces it. A row the
   * server did not score sorts to the bottom on 0 rather than reshuffling the rows it
   * did score, and the id tiebreak keeps even that deterministic.
   */
  rankOf?: (row: T) => number,
  /** Break ties in `rankOf` by `timeOf`, newest first, before the id. For a
   *  ranked order the route itself breaks that way (Trends sorted by views or
   *  likes: `sum DESC, createdAt DESC`); off by default, so a search keeps the
   *  id tiebreak it always had. */
  rankTiesByTime = false,
): FanoutMerge<T> {
  const failed = parts.reduce((n, part) => n + (part.failed ? 1 : 0), 0);
  const isError = parts.length > 0 && failed === parts.length;

  const seen = new Set<string>();
  const rows: T[] = [];
  for (const part of parts) {
    for (const row of part.rows) {
      const id = idOf(row);
      /* Two picked values can both hold the same row — a trend with examples on
         both platforms answers to both source requests — so the id is the merge
         key and the first sighting wins. */
      if (seen.has(id)) continue;
      seen.add(id);
      rows.push(row);
    }
  }

  /* The id breaks ties. Two rows created in the same millisecond — or scored to the
     same cosine — come back in whatever order their constituents happened to answer
     in, and without a tiebreak the wall would reshuffle them between renders. The
     route's own ORDER BY carries the same tiebreak on the search path and none on the
     browse path, so this is a STRICTER order than the server's, never a different one. */
  rows.sort((a, b) =>
    rankOf
      ? rankOf(b) - rankOf(a) ||
        (rankTiesByTime ? timeOf(b) - timeOf(a) : 0) ||
        idOf(a).localeCompare(idOf(b))
      : timeOf(b) - timeOf(a) || idOf(a).localeCompare(idOf(b)),
  );

  const totals = parts
    .map((part) => part.total)
    .filter((total): total is number => total !== null);

  return {
    rows,
    count: Math.max(rows.length, ...totals),
    atLeast: capped || failed > 0 || parts.some((part) => part.truncated),
    /* A constituent with no answer yet means the merged set is unread. Painting
       it would be a wall that grows under the reader one request at a time,
       which is the flicker the skeleton exists to prevent. */
    isLoading: !isError && parts.some((part) => part.pending),
    isFetching: parts.some((part) => part.fetching),
    isError,
    failed,
    refetch: () => {
      for (const part of parts) part.refetch();
    },
  };
}

/**
 * What a wall renders from, whichever path answered it.
 *
 * One shape for both, so the page's whole render tree reads the same fields
 * whether the route paginated for it or this file did.
 */
export interface WallView<T> {
  /** The rows on the page being drawn, already sliced. */
  rows: T[];
  pageCount: number;
  /** The figure the readout and the footer print. */
  count: number;
  /** `count` is a floor. Every place that prints it has to say so. */
  atLeast: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  /** Constituent reads that failed while others answered. */
  failed: number;
  /** How many requests this wall state issues. One is the single-request path. */
  requested: number;
  refetch: () => void;
}

/**
 * A merged fan-out as the wall reads it.
 *
 * PAGINATION, HONESTLY. Each constituent paginates on its own, so their pages
 * cannot be lined up: page 2 of one value and page 2 of another are not two
 * halves of page 2 of the union. Rather than stitching offsets that no route
 * agrees to, each request reads ONE WINDOW at the widest size the route accepts,
 * and the merged set is paged HERE.
 *
 * The exact condition under which the merged wall is still incomplete: a
 * constituent whose own reported total exceeds the window read for it (100 rows
 * on Trends, `v_basePagination`'s cap; 60 on References, `MAX_PAGE_SIZE`), or a
 * plan cut at MAX_FANOUT, or a constituent that failed. Every one of those sets
 * `atLeast`, and the wall then reports a floor instead of a total.
 *
 * Pages come off the rows actually READ, never off the floor: a page the merge
 * cannot fill is a page that would answer with nothing.
 */
export function mergedWallView<T>(
  merged: FanoutMerge<T>,
  page: number,
  pageSize: number,
  requested: number,
): WallView<T> {
  const pageCount = Math.max(1, Math.ceil(merged.rows.length / pageSize));
  const current = Math.min(Math.max(1, page), pageCount);
  const start = (current - 1) * pageSize;
  return {
    rows: merged.rows.slice(start, start + pageSize),
    pageCount,
    count: merged.count,
    atLeast: merged.atLeast,
    isLoading: merged.isLoading,
    isFetching: merged.isFetching,
    isError: merged.isError,
    failed: merged.failed,
    requested,
    refetch: merged.refetch,
  };
}

/**
 * One combination per request.
 *
 * An axis holding nothing narrows nothing, so it contributes a single
 * `undefined` and the product does not grow. An axis holding one value
 * contributes that value, which is why a one-value pick still comes out as
 * exactly ONE request with the parameter sent server-side, unchanged.
 *
 * THE CROSS PRODUCT IS THE WHOLE PRODUCT, not the biggest axis with the rest
 * narrowed on the client. `(a OR b) AND (x OR y)` distributes into four exact
 * server-side reads, and every one of them is a whole-library answer; narrowing
 * the second axis on the client would put back exactly the page-scoped filter
 * this file exists to remove, on that axis. The product is small enough to say
 * yes to: four kinds by two sources is 8, and MAX_FANOUT is the ceiling.
 */
export function axisProduct<A, B>(
  first: readonly A[],
  second: readonly B[],
): { first: A | undefined; second: B | undefined }[] {
  const a: (A | undefined)[] = first.length > 0 ? [...first] : [undefined];
  const b: (B | undefined)[] = second.length > 0 ? [...second] : [undefined];
  const out: { first: A | undefined; second: B | undefined }[] = [];
  for (const one of a) {
    for (const two of b) out.push({ first: one, second: two });
  }
  return out;
}
